import { Reorder } from 'framer-motion';
import { ChevronLeft, Plus } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useApiRequest from '../../../../lib/hooks/useRequest';
import {
  CourseDetailsAdmin,
  CourseLessonDetails,
  CourseLessonTreeNode,
  CourseModuleTreeNode,
  CreateCourseLessonRequest,
  CreateCourseModuleRequest,
  ReorderCourseLessonsRequest,
  ReorderCourseModulesRequest,
  UpdateCourseLessonRequest,
  UpdateCourseModuleRequest,
  UpdateCourseReleaseRequest,
  UpdateCourseRequest
} from '../../../../lib/interfaces/courses';
import { Button } from '../../../../ui/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../../ui/components/ui/dialog';
import Preloader from '../../../../ui/components/ui/preloader';
import CourseFormDialog, { CourseFormSubmitPayload } from '../components/course-form-dialog/course-form-dialog';
import CourseHeader from './components/course-header/course-header';
import LessonFormDialog from './components/lesson-form-dialog/lesson-form-dialog';
import ModuleRow, { ModuleRowSkeleton } from './components/module-row/module-row';

const REORDER_DEBOUNCE_MS = 400;

interface LessonDeleteTarget {
  moduleId: string;
  lesson: CourseLessonTreeNode;
}

interface CourseBuilderPageProps {
  courseId: string;
}

const CourseBuilderPage = ({ courseId }: CourseBuilderPageProps) => {
  const navigate = useNavigate();
  const { apiRequest, apiFileRequest } = useApiRequest();

  const [isLoading, setIsLoading] = useState(true);
  const [course, setCourse] = useState<CourseDetailsAdmin | null>(null);

  const [metaDialogOpen, setMetaDialogOpen] = useState(false);
  const [isTogglingRelease, setIsTogglingRelease] = useState(false);
  const [moduleToDelete, setModuleToDelete] = useState<CourseModuleTreeNode | null>(null);
  const [isDeletingModule, setIsDeletingModule] = useState(false);

  /** Set for both create (lesson: null) and edit — the module id is what the submit needs either way. */
  const [lessonDialog, setLessonDialog] = useState<{ moduleId: string; lessonId: string | null } | null>(null);
  const [editingLesson, setEditingLesson] = useState<CourseLessonDetails | null>(null);
  const [isLoadingLesson, setIsLoadingLesson] = useState(false);

  const [lessonToDelete, setLessonToDelete] = useState<LessonDeleteTarget | null>(null);
  const [isDeletingLesson, setIsDeletingLesson] = useState(false);

  const reorderTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** One debounce timer per module — reordering lessons in module A must not cancel module B's pending call. */
  const lessonReorderTimeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  /**
   * `silent` refetches keep the skeleton branch out of the render — that branch unmounts the module
   * rows, which would reset their expand state. Mutations always refetch silently.
   */
  const fetchCourse = useCallback(
    async (options?: { silent?: boolean }) => {
      if (!options?.silent) setIsLoading(true);
      try {
        const res = await apiRequest<CourseDetailsAdmin>(`courses/admin/${courseId}`, 'GET');
        if (res.data) setCourse(res.data);
      } finally {
        if (!options?.silent) setIsLoading(false);
      }
    },
    [apiRequest, courseId]
  );

  useEffect(() => {
    fetchCourse();
  }, [fetchCourse]);

  useEffect(() => {
    const lessonTimeouts = lessonReorderTimeoutsRef.current;
    return () => {
      if (reorderTimeoutRef.current) clearTimeout(reorderTimeoutRef.current);
      lessonTimeouts.forEach((timeout) => clearTimeout(timeout));
    };
  }, []);

  const handleMetaSubmit = async (payload: CourseFormSubmitPayload) => {
    const updateBody: UpdateCourseRequest = payload.data;
    await apiRequest(`courses/admin/${courseId}`, 'PATCH', {
      body: updateBody,
      toast: { toastText: 'Kurs wurde aktualisiert.' }
    });
    if (payload.removeExistingImage && !payload.file) {
      await apiRequest(`courses/admin/${courseId}/image`, 'DELETE');
    }
    if (payload.file) {
      await apiFileRequest(`courses/admin/${courseId}/image`, 'POST', payload.file, { formdataName: 'file' });
    }
    await fetchCourse({ silent: true });
  };

  const handleToggleRelease = async (nextIsReleased: boolean) => {
    setIsTogglingRelease(true);
    try {
      const body: UpdateCourseReleaseRequest = { isReleased: nextIsReleased };
      const res = await apiRequest<{ isReleased: boolean }>(`courses/admin/${courseId}/release`, 'PATCH', {
        body,
        toast: { toastText: nextIsReleased ? 'Kurs ist jetzt veröffentlicht.' : 'Kurs ist wieder ein Entwurf.' }
      });
      // Only follow the server — a rejected release (e.g. no lessons) must not flip the switch.
      if (res.data) setCourse((prev) => (prev ? { ...prev, isReleased: res.data!.isReleased } : prev));
    } finally {
      setIsTogglingRelease(false);
    }
  };

  const handleAddModule = async () => {
    if (!course) return;
    const body: CreateCourseModuleRequest = { title: 'Neues Modul' };
    await apiRequest(`courses/admin/${courseId}/modules`, 'POST', {
      body,
      toast: { toastText: 'Modul wurde hinzugefügt.' }
    });
    await fetchCourse({ silent: true });
  };

  const handleModuleTitleChange = async (moduleId: string, title: string) => {
    const body: UpdateCourseModuleRequest = { title };
    await apiRequest(`courses/admin/${courseId}/modules/${moduleId}`, 'PATCH', { body });
    setCourse((prev) => (prev ? { ...prev, modules: prev.modules.map((m) => (m._id === moduleId ? { ...m, title } : m)) } : prev));
  };

  const handleModuleDeleteConfirm = async () => {
    if (!moduleToDelete) return;
    setIsDeletingModule(true);
    try {
      await apiRequest(`courses/admin/${courseId}/modules/${moduleToDelete._id}`, 'DELETE', {
        toast: { toastText: 'Modul wurde gelöscht.' }
      });
      setModuleToDelete(null);
      await fetchCourse({ silent: true });
    } finally {
      setIsDeletingModule(false);
    }
  };

  const handleReorder = (nextOrder: CourseModuleTreeNode[]) => {
    setCourse((prev) => (prev ? { ...prev, modules: nextOrder } : prev));
    if (reorderTimeoutRef.current) clearTimeout(reorderTimeoutRef.current);
    reorderTimeoutRef.current = setTimeout(() => {
      const body: ReorderCourseModulesRequest = { moduleIds: nextOrder.map((m) => m._id) };
      apiRequest(`courses/admin/${courseId}/modules/reorder`, 'POST', { body });
    }, REORDER_DEBOUNCE_MS);
  };

  const handleOpenCreateLesson = (moduleId: string) => {
    setEditingLesson(null);
    setLessonDialog({ moduleId, lessonId: null });
  };

  const handleOpenEditLesson = async (moduleId: string, lesson: CourseLessonTreeNode) => {
    setEditingLesson(null);
    setLessonDialog({ moduleId, lessonId: lesson._id });
    setIsLoadingLesson(true);
    try {
      const res = await apiRequest<CourseLessonDetails>(`courses/admin/${courseId}/modules/${moduleId}/lessons/${lesson._id}`, 'GET');
      if (res.data) setEditingLesson(res.data);
    } finally {
      setIsLoadingLesson(false);
    }
  };

  const handleCloseLessonDialog = () => {
    setLessonDialog(null);
    setEditingLesson(null);
  };

  const handleLessonSubmit = async (data: CreateCourseLessonRequest) => {
    if (!lessonDialog) return;
    const { moduleId, lessonId } = lessonDialog;

    if (lessonId) {
      // Send null for cleared fields so the server actually unsets them — undefined would be a no-op on PATCH.
      const body: UpdateCourseLessonRequest = {
        title: data.title,
        heading: data.heading,
        videoUrl: data.videoUrl ?? '',
        textHtml: data.textHtml ?? ''
      };
      await apiRequest(`courses/admin/${courseId}/modules/${moduleId}/lessons/${lessonId}`, 'PATCH', {
        body,
        toast: { toastText: 'Lektion wurde gespeichert.' }
      });
    } else {
      await apiRequest(`courses/admin/${courseId}/modules/${moduleId}/lessons`, 'POST', {
        body: data,
        toast: { toastText: 'Lektion wurde hinzugefügt.' }
      });
    }

    await fetchCourse({ silent: true });
  };

  // Uploads a body image for the lesson currently open in the dialog. The endpoint is scoped to the
  // module, not the lesson, so a not-yet-saved lesson can already carry images.
  const handleLessonImageUpload = async (file: File): Promise<{ path: string; url: string }> => {
    const moduleId = lessonDialog?.moduleId;
    if (!moduleId) throw new Error('No module selected');

    const res = await apiFileRequest<{ path: string; url: string }>(
      `courses/admin/${courseId}/modules/${moduleId}/lessons/images`,
      'POST',
      file,
      { formdataName: 'file' }
    );
    if (!res.data) throw new Error('Upload failed');
    return res.data;
  };

  const handleLessonTitleChange = async (moduleId: string, lessonId: string, title: string) => {
    const body: UpdateCourseLessonRequest = { title };
    await apiRequest(`courses/admin/${courseId}/modules/${moduleId}/lessons/${lessonId}`, 'PATCH', { body });
    setCourse((prev) =>
      prev
        ? {
            ...prev,
            modules: prev.modules.map((m) =>
              m._id === moduleId ? { ...m, lessons: m.lessons.map((l) => (l._id === lessonId ? { ...l, title } : l)) } : m
            )
          }
        : prev
    );
  };

  const handleLessonDeleteConfirm = async () => {
    if (!lessonToDelete) return;
    setIsDeletingLesson(true);
    try {
      await apiRequest(`courses/admin/${courseId}/modules/${lessonToDelete.moduleId}/lessons/${lessonToDelete.lesson._id}`, 'DELETE', {
        toast: { toastText: 'Lektion wurde gelöscht.' }
      });
      setLessonToDelete(null);
      await fetchCourse({ silent: true });
    } finally {
      setIsDeletingLesson(false);
    }
  };

  const handleLessonsReorder = (moduleId: string, nextOrder: CourseLessonTreeNode[]) => {
    setCourse((prev) => (prev ? { ...prev, modules: prev.modules.map((m) => (m._id === moduleId ? { ...m, lessons: nextOrder } : m)) } : prev));

    const timeouts = lessonReorderTimeoutsRef.current;
    const pending = timeouts.get(moduleId);
    if (pending) clearTimeout(pending);

    timeouts.set(
      moduleId,
      setTimeout(() => {
        const body: ReorderCourseLessonsRequest = { lessonIds: nextOrder.map((l) => l._id) };
        apiRequest(`courses/admin/${courseId}/modules/${moduleId}/lessons/reorder`, 'POST', { body });
        timeouts.delete(moduleId);
      }, REORDER_DEBOUNCE_MS)
    );
  };

  if (isLoading) {
    return (
      <>
        <div className="h-7 w-40 bg-[#E4E7EC] rounded-full animate-pulse" />
        <div className="bg-white rounded-2xl border border-[#E4E7EC] h-28 animate-pulse" />
        <div className="flex flex-col gap-3">
          <ModuleRowSkeleton />
          <ModuleRowSkeleton />
          <ModuleRowSkeleton />
        </div>
      </>
    );
  }

  if (!course) {
    return <Preloader />;
  }

  return (
    <>
      <button
        type="button"
        onClick={() => navigate('/learning-suite/admin')}
        className="flex items-center gap-1 text-[14px] text-[#888] hover:text-[#12282A] self-start">
        <ChevronLeft className="w-4 h-4" />
        zur Übersicht
      </button>

      <CourseHeader
        course={course}
        onEditMeta={() => setMetaDialogOpen(true)}
        onToggleRelease={handleToggleRelease}
        isTogglingRelease={isTogglingRelease}
      />

      <div className="flex items-center justify-between mt-2">
        <h2 className="text-[18px] font-bold text-[#12282A]">Module</h2>
        <Button onClick={handleAddModule} className="flex gap-2">
          <Plus className="w-4 h-4" />
          Modul hinzufügen
        </Button>
      </div>

      {course.modules.length === 0 ? (
        <div className="bg-white rounded-2xl border border-[#E4E7EC] p-10 text-center">
          <p className="text-[16px] text-[#12282A] font-bold mb-1">Noch keine Module</p>
          <p className="text-[14px] text-[#888]">Lege das erste Modul an, um den Kurs zu strukturieren.</p>
        </div>
      ) : (
        <Reorder.Group axis="y" values={course.modules} onReorder={handleReorder} className="flex flex-col gap-3" as="div">
          {course.modules.map((module, i) => (
            <ModuleRow
              key={module._id}
              index={i + 1}
              module={module}
              sortable
              onTitleChange={handleModuleTitleChange}
              onDelete={setModuleToDelete}
              onAddLesson={handleOpenCreateLesson}
              onLessonTitleChange={handleLessonTitleChange}
              onLessonEdit={handleOpenEditLesson}
              onLessonDelete={(moduleId, lesson) => setLessonToDelete({ moduleId, lesson })}
              onLessonsReorder={handleLessonsReorder}
            />
          ))}
        </Reorder.Group>
      )}

      <CourseFormDialog
        isOpen={metaDialogOpen}
        onOpenChange={setMetaDialogOpen}
        course={course}
        onSubmit={handleMetaSubmit}
      />

      <LessonFormDialog
        isOpen={!!lessonDialog}
        onOpenChange={(open) => !open && handleCloseLessonDialog()}
        moduleTitle={course.modules.find((module) => module._id === lessonDialog?.moduleId)?.title ?? ''}
        isEdit={!!lessonDialog?.lessonId}
        lesson={editingLesson}
        isLoadingLesson={isLoadingLesson}
        onImageUpload={handleLessonImageUpload}
        onSubmit={handleLessonSubmit}
      />

      <Dialog open={!!moduleToDelete} onOpenChange={(open) => !open && setModuleToDelete(null)}>
        <DialogContent className="sm:max-w-md min-w-[440px] bg-white" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="text-[22px] font-bold text-[#12282A]">Modul wirklich löschen?</DialogTitle>
            <DialogDescription className="text-[14px] text-[#888]">
              „{moduleToDelete?.title}“ wird unwiderruflich gelöscht
              {moduleToDelete && moduleToDelete.lessons.length > 0
                ? ` – inklusive ${moduleToDelete.lessons.length} ${moduleToDelete.lessons.length === 1 ? 'Lektion' : 'Lektionen'}.`
                : '.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-3">
            <Button variant="outline" onClick={() => setModuleToDelete(null)} disabled={isDeletingModule}>
              Abbrechen
            </Button>
            <Button variant="destructive" onClick={handleModuleDeleteConfirm} disabled={isDeletingModule}>
              {isDeletingModule ? 'Löscht…' : 'Löschen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!lessonToDelete} onOpenChange={(open) => !open && setLessonToDelete(null)}>
        <DialogContent className="sm:max-w-md min-w-[440px] bg-white" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="text-[22px] font-bold text-[#12282A]">Lektion wirklich löschen?</DialogTitle>
            <DialogDescription className="text-[14px] text-[#888]">
              „{lessonToDelete?.lesson.title}“ wird unwiderruflich gelöscht.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-3">
            <Button variant="outline" onClick={() => setLessonToDelete(null)} disabled={isDeletingLesson}>
              Abbrechen
            </Button>
            <Button variant="destructive" onClick={handleLessonDeleteConfirm} disabled={isDeletingLesson}>
              {isDeletingLesson ? 'Löscht…' : 'Löschen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CourseBuilderPage;
